import {
  MultiAxisDimSelection,
  MultiSelectionBox,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';

/**
 * Per-row selection state derived from the current table selection.
 */
export interface RowSelection {
  /**
   * The minimum selected column index for this row, or null when the row is not
   * within the selection.
   */
  selectedColIndexMin: number | null;
  /**
   * The maximum selected column index for this row, or null when the row is not
   * within the selection.
   */
  selectedColIndexMax: number | null;
  /**
   * The active (focused) column index for this row, or null when the active cell
   * is not on this row (and this row is not part of a merged region owned by the
   * active cell).
   */
  selectedColIndexActive: number | null;
  /**
   * Whether this row's last selected cell should render the fill-down square.
   */
  hasFillDownSquare: boolean;
  /**
   * Minimum row index of the current row-axis selection when this row is selected,
   * or null when the row is outside the selected row range.
   */
  selectionRangeMinIndex: number | null;
  /**
   * Maximum row index of the current row-axis selection when this row is selected,
   * or null when the row is outside the selected row range.
   */
  selectionRangeMaxIndex: number | null;
}

/** Whether the row is within the vertical span of the (merge-expanded) selection box. */
function _isRowWithinSelection(rowIndex: number, selectionBox: MultiSelectionBox): boolean {
  return rowIndex >= selectionBox.minI && rowIndex <= selectionBox.maxI;
}

/** The minimum selected column index for the row, or null when the row is outside the selection. */
export function computeSelectedColIndexMin(
  rowIndex: number,
  selectionBox: MultiSelectionBox | null
): number | null {
  if (selectionBox && _isRowWithinSelection(rowIndex, selectionBox)) {
    return selectionBox.minJ;
  }
  return null;
}

/** The maximum selected column index for the row, or null when the row is outside the selection. */
export function computeSelectedColIndexMax(
  rowIndex: number,
  selectionBox: MultiSelectionBox | null
): number | null {
  if (selectionBox && _isRowWithinSelection(rowIndex, selectionBox)) {
    return selectionBox.maxJ;
  }
  return null;
}

/**
 * Whether the active (focused) cell is on this row — either because it lives on
 * this row directly, or because this row is part of a merged region owned by the
 * active cell.
 */
function _isActiveCellOnRow(
  rowIndex: number,
  multiSelIndex: TableIndex,
  computedTable: Array<Array<EvaluatedCell>>
): boolean {
  const [activeRowIndex, activeColIndex] = multiSelIndex;
  if (activeRowIndex === rowIndex) {
    return true;
  }
  const mergedBox = computedTable[rowIndex]?.[activeColIndex]?.mergedBox;
  return (
    mergedBox != null &&
    mergedBox.minJ === activeColIndex &&
    mergedBox.minI <= activeRowIndex &&
    mergedBox.maxI >= activeRowIndex
  );
}

/** The active column index for the row, or null when the active cell is not on this row. */
export function computeSelectedColIndexActive(
  rowIndex: number,
  multiSelIndex: TableIndex | null,
  computedTable: Array<Array<EvaluatedCell>>
): number | null {
  if (multiSelIndex != null && _isActiveCellOnRow(rowIndex, multiSelIndex, computedTable)) {
    return multiSelIndex[1];
  }
  return null;
}

/** Whether the row is the last (bottom) row of the selection. */
function _isLastRowOfSelection(rowIndex: number, selectionBox: MultiSelectionBox): boolean {
  return rowIndex === selectionBox.maxI;
}

/** Whether this row's last selected cell should render the fill-down square. */
export function computeHasFillDownSquare(
  rowIndex: number,
  {
    selectionBox,
    tableHasActiveCell,
    isTableLocked,
    multiActive,
    isFillDownEnabled,
  }: {
    /** The (merge-expanded) selection box, or null when there is no selection. */
    selectionBox: MultiSelectionBox | null;
    /**
     * Whether the table currently has any active (editing) cell. This is a
     * table-wide flag and is distinct from `RowModel.hasActiveCell`, which means
     * "the active cell is on this row".
     */
    tableHasActiveCell: boolean;
    isTableLocked: boolean;
    /** Whether the selection is actively being updated (drag in progress). */
    multiActive: string | boolean | null;
    isFillDownEnabled: boolean;
  }
): boolean {
  return (
    selectionBox != null &&
    _isLastRowOfSelection(rowIndex, selectionBox) &&
    !tableHasActiveCell &&
    !isTableLocked &&
    !multiActive &&
    isFillDownEnabled
  );
}

/** Whether `rowIndex` is within the current row-axis selection range. */
function _isRowIndexWithinRowSelection(rowIndex: number, rowSelection: [number, number]): boolean {
  const [minIndex, maxIndex] = rowSelection;
  return minIndex <= rowIndex && rowIndex <= maxIndex;
}

/** Minimum row index of the row-axis selection when this row is selected, or null. */
export function computeRowSelectionRangeMinIndex(
  multiAxisDimSelection: MultiAxisDimSelection,
  rowIndex: number
): number | null {
  const rowSelection = multiAxisDimSelection.row;
  if (rowSelection != null && _isRowIndexWithinRowSelection(rowIndex, rowSelection)) {
    return rowSelection[0];
  }
  return null;
}

/** Maximum row index of the row-axis selection when this row is selected, or null. */
export function computeRowSelectionRangeMaxIndex(
  multiAxisDimSelection: MultiAxisDimSelection,
  rowIndex: number
): number | null {
  const rowSelection = multiAxisDimSelection.row;
  if (rowSelection != null && _isRowIndexWithinRowSelection(rowIndex, rowSelection)) {
    return rowSelection[1];
  }
  return null;
}

export const _forTesting = {
  isRowWithinSelection: _isRowWithinSelection,
  isActiveCellOnRow: _isActiveCellOnRow,
  isLastRowOfSelection: _isLastRowOfSelection,
  isRowIndexWithinRowSelection: _isRowIndexWithinRowSelection,
};
